'use client'

import { useEffect, useRef, type HTMLProps } from 'react'

/**
 * Data encoded inside a Google gsi credential token.
 *
 * @see https://developers.google.com/identity/gsi/web/reference/html-reference#credential
 */
export interface GoogleCredentialsPayload {
  /** Issuer of the JWT */
  iss: string
  /** Time before which the JWT must not be accepted for processing */
  nbf: number
  /** Your server's client ID. */
  aud: string
  /** The unique ID of the user's Google Account */
  sub: string
  /** If present, the host domain of the user's GSuite email address */
  hd?: string
  /** The user's email address */
  email: string
  /** true, if Google has verified the email address */
  email_verified: true
  /** The party to which the ID Token was issued. */
  azp: string
  /** The user's full name */
  name: string
  /** If present, a URL to user's profile picture */
  picture?: string
  /** The user's given name */
  given_name: string
  /** The user's family name */
  family_name: string
  /** Unix timestamp of the assertion's creation time */
  iat: number
  /** Unix timestamp of the assertion's expiration time */
  exp: number
  /** Unique identifier; can be used to prevent the JWT from being replayed. */
  jti: string
}

/**
 * Determines whether the provided response is a valid Google gsi
 * credentials response (i.e.: an object containing a token).
 */
function isGoogleSingInSuccessEvent(response: unknown): response is CustomEvent<{ credential: string }> {
  return (
    response instanceof CustomEvent &&
    response.type === 'GoogleSignInSuccess' &&
    typeof response.detail.credential === 'string'
  )
}

/**
 * Decodes the provided Google gsi credential token into its payload.
 * @see https://developers.google.com/identity/gsi/web/reference/html-reference#credential
 */
function decodeCredential(credential: string): GoogleCredentialsPayload {
  try {
    const encodedPayload = credential.split('.')[1]
    const payload = Buffer.from(encodedPayload ?? '', 'base64').toString()
    return JSON.parse(payload)
  } catch (err: unknown) {
    throw new Error('Failed to parse Google credentials response', { cause: err })
  }
}

export interface GoogleSignInButtonProps extends HTMLProps<HTMLDivElement> {
  /**
   * Whenever the user logins with a google account, collect the credentials
   * and handle them via this callback.
   */
  onCredentialsResponse?: (credentials: { token: string } & GoogleCredentialsPayload) => void
}

/**
 * A Google Sing-in button
 */
export default function GoogleSignInButton(props: GoogleSignInButtonProps) {
  const { onCredentialsResponse, ...otherProps } = props

  const buttonRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = window as any
    const renderButton = win?.google?.accounts?.id?.renderButton
    const options = document.getElementById('g_id_onload')?.dataset

    if (typeof renderButton === 'function') {
      renderButton(buttonRef.current, {
        ...options,
        // Not really sure why, but not passing this here, causes the button to not
        // take the full width of the container.
        width: 320,
      })
    }
  }, [])

  // Enure we can pass a stable dependency to the useEffect below
  const onCredentialsResponseRef = useRef(onCredentialsResponse)
  onCredentialsResponseRef.current = onCredentialsResponse

  // Define the callback on a global level
  useEffect(() => {
    const handleSuccess = (ev: Event) => {
      console.debug('Google sign-in success', ev)
      if (isGoogleSingInSuccessEvent(ev)) {
        const token = ev.detail.credential
        const credential = decodeCredential(token)
        onCredentialsResponseRef.current?.({ token, ...credential })
      }
    }

    document.addEventListener('GoogleSignInSuccess', handleSuccess)
    return () => {
      document.removeEventListener('GoogleSignInSuccess', handleSuccess)
    }
  }, [])

  return (
    <div
      {...otherProps}
      ref={buttonRef}
      className="g_id_signin"
      /**
       * For a full reference of available options see:
       * https://developers.google.com/identity/gsi/web/reference/html-reference#element_with_class_g_id_signin
       */
      data-type="standard"
      data-shape="rectangular"
      data-theme="outline"
      data-text="continue_with"
      data-size="large"
      data-logo_alignment="center"
      data-width="320"
    />
  )
}
